import {
  Globe,
  EllipsisVertical,
  LogOut,
  Home,
  CreditCard,
  MessageSquare,
  MessageCircle,
  Users,
  FileBarChart,
  FileDiff,
} from 'lucide-react';

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/ui/sidebar';
import { useLocation, useNavigate } from 'react-router-dom';
import { AppRouters } from '@/utils/constants/routers';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from './avatar';
import { useAuth } from '@/hooks/useAuth';
import { truncateString } from '@/utils/functions/stringFormat';
import { Separator } from './separator';

const items = [
  {
    title: 'Painel de Traduções',
    url: AppRouters.translatePanel,
    icon: Globe,
  },
  {
    title: 'Gestão de planos',
    url: AppRouters.planManagement,
    icon: CreditCard,
  },
  {
    title: 'Conhecimento do Chat',
    url: AppRouters.chatKnowledge,
    icon: MessageSquare,
  },
  {
    title: 'Conversas',
    url: AppRouters.chat,
    icon: MessageCircle,
  },
  {
    title: 'Usuários',
    url: AppRouters.users,
    icon: Users,
  },
  {
    title: 'Replicação BigQuery',
    url: AppRouters.bigQuerySyncPreview,
    icon: FileDiff,
  },
  {
    title: 'Análise de projetos',
    url: AppRouters.projectAnalysisAdmin,
    icon: FileBarChart,
  },
];

export function AppSidebar() {
  const navigate = useNavigate();
  const location = useLocation();

  const isActive = (url: string) => location.pathname === url;

  const { user, logoutAdmin } = useAuth();

  const getFirstTwoNames = (fullName: string | null | undefined): string => {
    if (!fullName) return '';
    const names = fullName.trim().split(' ');
    const firstTwoNames = names.slice(0, 2).join(' ');
    return truncateString(firstTwoNames, 16);
  };

  return (
    <Sidebar variant="sidebar" collapsible="icon">
      <SidebarHeader className="border-b border-gray-200 bg-white p-4">
        <SidebarMenu>
          <a href={AppRouters.translatePanel}>
            <img
              src="/img/png/logos/logo_kyt.png"
              alt="logo"
              className="w-[90px] h-10 object-contain"
            />
          </a>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent className="bg-white">
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {items.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild isActive={isActive(item.url)}>
                    <a href={item.url}>
                      <item.icon />
                      <span>{item.title}</span>
                    </a>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="bg-white">
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton className="w-full h-full">
                  <Avatar className="rounded-lg">
                    <AvatarImage
                      src={user?.photoURL || ''}
                      alt={user?.displayName || ''}
                      className="bg-gray-200 rounded-lg"
                    />
                    <AvatarFallback className="bg-gray-200 rounded-lg">
                      {user?.displayName?.charAt(0)}
                    </AvatarFallback>
                  </Avatar>

                  <div className="flex flex-col">
                    <p className="text-sm font-medium text-neutral-950">
                      {getFirstTwoNames(user?.displayName)}
                    </p>
                    <p className="text-sm text-slate-500">
                      {truncateString(user?.email || '', 16)}
                    </p>
                  </div>
                  <EllipsisVertical className="ml-auto" />
                </SidebarMenuButton>
              </DropdownMenuTrigger>

              <DropdownMenuContent side="top" className="w-[--radix-popper-anchor-width]">
                <DropdownMenuItem
                  onClick={() => {
                    navigate(AppRouters.home);
                    window.location.reload();
                  }}
                >
                  <Home />
                  <span>Back to platform</span>
                </DropdownMenuItem>
                <Separator />
                <DropdownMenuItem onClick={logoutAdmin}>
                  <LogOut />
                  <span>Logout</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
